import { useRef } from 'react';
import { useLoaderData } from 'react-router';
import { useCategoryTilesTracking } from '~/ui-rocco/hooks/use-category-tiles-tracking';
import { useHydrated } from 'remix-utils';
import { cn } from '@rocco/utils/cn';

import { useAppDisplayModeContext } from '@archipro-design/aria';
import { VariantSelectionProvider } from '@modules/product/context/VariantSelectionContext';
import { VariantOptionProvider } from '@modules/product/context/VariantOptionContext';
import { useTrackProductView } from '@modules/product/hook/use-track-product-view';
import type { loader } from '~/routes/_app.product.$urlSegment';
import { SectionSpacer } from '~/ui-rocco/pages/home-page/components/SectionSpacer';
import { ProductHeroSection } from './sections/ProductHeroSection';
import { ProductGallerySection } from './sections/ProductGallerySection';
import { NextProductTransition } from './sections/NextProductTransition';
import { ProductBreadcrumbBar } from './sections/ProductBreadcrumbBar';
import { ProductPurchaseSection } from './sections/ProductPurchaseSection';
import { ProductCreditsRow } from './sections/ProductCreditsRow';
import { ProductBackdropMark } from './sections/ProductBackdropMark';
import { ProductSupplierLogoBadge } from './sections/ProductSupplierLogoBadge';
import { ProductDetailsSection } from './sections/ProductDetailsSection';
import { ProductDownloadsSection } from './sections/ProductDownloadsSection';
import { ProductGetSpecificSection } from './sections/ProductGetSpecificSection';
import { MoreProductsSection } from './sections/MoreProductsSection';
import { ProductFaqSection } from './sections/ProductFaqSection';
import { ProductInstallersSection } from './sections/ProductInstallersSection';
import { MoreInCategorySection } from './sections/MoreInCategorySection';
import { InTheWildSection } from './sections/InTheWildSection';
import { ProductTestimonialSection } from './sections/ProductTestimonialSection';
import { ProductSpecAccordionSection } from './sections/ProductSpecAccordionSection';
import { SpecifiedInSection } from './sections/SpecifiedInSection';
import {
    DetailMobileAppChrome,
    IslandFreeClosing,
} from '~/ui-rocco/pages/detail-shared';
import { ProductDetailsMagicIsland } from './magic-island/ProductDetailsMagicIsland';
import { BackToTop } from '~/ui-rocco/components/BackToTop';
import { useNextProductIslandMode } from './magic-island/use-next-product-island-mode';
import { useCategoryNeighbours } from '@modules/product/hook/use-category-neighbours';
import { ProductPageCtasProvider } from './ProductPageCtasProvider';

/**
 * Null component — fires the ViewProduct analytics inside the
 * variant provider (so it sees the selected variant), matching the legacy PDP.
 */
const ProductViewTracker = () => {
    const { product } = useLoaderData<typeof loader>();
    useTrackProductView(product);
    return null;
};

/**
 * BC-4505 — revamped editorial product details page.
 *
 * Built section-by-section per `plans/BC-4505-details-page-revamp.md`; the
 * loader is shared with the legacy page and stays unchanged. Rendered in place
 * of the legacy page behind the `revamp_2026` feature flag (or
 * `?revamp=1` in local dev).
 *
 * Desktop and mobile order their sections differently (BC-4524, Figma
 * 1855:13305): mobile has no text hero (the product title lives in the
 * purchase block) and runs Gallery → Purchase → Accordion → related carousels
 * → Tagged statement → About the Supplier → also-viewed grid → closing. The
 * display mode comes from server device detection, so the branch is
 * SSR-consistent.
 */
export const ProductPageRevamp = () => {
    const { product, featuredProjects } = useLoaderData<typeof loader>();
    // Puts this page's tile callbacks in the shared atom the Rocco tiles read.
    // Without it every rail's `section` is set and then dropped: the tile calls
    // `trackingConfig?.onClick?.()` on an empty context, so the section-named
    // event the legacy page reported never fires and only the tile's own
    // `TileMediaDetailClick` gets out. No grid ref — the grid position is
    // best-effort and this page's rails are carousels, not a measurable grid.
    useCategoryTilesTracking(useRef<HTMLElement>(null));

    const { mobile } = useAppDisplayModeContext();
    // The products either side of this one in its category, fetched on
    // arrival rather than by the page's loader — the lookup has no business
    // on the page's first byte. Fetched once here for both offers: the foot's
    // turn and the island's pill.
    const neighbours = useCategoryNeighbours(
        product.Categories?.[0]?.Link,
        product.ID,
    );
    const nextProduct = neighbours?.next;
    // What the closing run offers instead of blanking the island.
    const nextProductIsland = useNextProductIslandMode(nextProduct);

    const hydrated = useHydrated();

    // Sections self-hide on empty data, so each conditional section OWNS its
    // leading spacer — otherwise a hidden section leaves its spacers stacked
    // as a dead gap. These predicates mirror the sections' own null checks.
    // Diagrams no longer count toward the gallery — they live in the spec
    // accordion's (02) Specs & Dimensions row.
    const hasGallery =
        (product.LibraryImages?.length ?? 0) > 0 ||
        (product.LibraryVideos?.length ?? 0) > 0;
    const hasTaggedProjects = featuredProjects.FeaturedProjects.edges.some(
        (edge) => edge.node.images?.[0]?.src,
    );

    // Everything below the gallery, shared by the page and overlay orders.
    const desktopBody = (
        <>
            {/* Half the design's "Page spacer / L": the badge now hangs into
                this gap, so 160px left the title stranded well below it. */}
            <SectionSpacer
                height={80}
                mobileHeight={14}
                className="bg-ground"
                unit="rem"
            />
            {/* The supplier's name, very large and soft, behind the band
                of summary, tags and variants, as project detail sets the
                practice's. Hung from a zero-height anchor rather than a
                wrapper: the purchase section's actions are sticky, and a
                wrapper would stop them at its own foot. */}
            <div className="relative h-0">
                <ProductBackdropMark />
            </div>
            {/* F6 variants live in the F4 ecom modal. */}
            <ProductPurchaseSection />
            {/* The tags and the variants, 64px under the summary as the
                project sets its team and suppliers. */}
            <SectionSpacer
                height={64}
                mobileHeight={32}
                className="bg-ground"
                unit="rem"
            />
            <ProductCreditsRow />
            {/* Details: half of it is one image, the spec list beside it. */}
            <ProductDetailsSection />
            <SectionSpacer
                height={80}
                mobileHeight={40}
                className="bg-ground"
                unit="rem"
            />
            {/* The files, on the page rather than folded into the spec list —
                a specifier comes for these, and a row that opens into five is
                a row that hides four. */}
            <ProductDownloadsSection />
            <ProductGetSpecificSection />
            {/* About the Supplier, Similar Products and the Get-in-Touch
                enquiry hook all live inside
                this accordion on desktop (auto-numbered rows); only mobile
                renders them standalone (see mobileOrder). The closing band
                follows, so the accordion keeps its hairline. */}
            <ProductSpecAccordionSection runIn="max-md:pt-6 md:pt-[3.75rem]" />
            {/* The three ways to reach the supplier, before the page turns
                to the rest of their work. */}
            <ProductInstallersSection runIn="max-md:pt-9 md:pt-[5.625rem]" />
            {/* Frequently asked questions, answered from the page's own data
                and linking out to the supplier, the category and the projects
                that use the product. */}
            <ProductFaqSection />
            {/* Once the reader is into the products that follow this one,
                the island stops offering this product and offers the next.
                One claim over the whole run: the carousels inside it make
                their own, and a claim per block flickers in the gaps. */}
            <IslandFreeClosing mode={nextProductIsland} priority={5}>
                {/* 150px under the FAQ, which keeps no padding of its own
                    below. Without the FAQ, drop the prop for the default. */}
                <MoreProductsSection runIn="max-md:pt-16 md:pt-[9.375rem]" />
                <SectionSpacer
                    height={120}
                    mobileHeight={48}
                    className="bg-ground"
                    unit="rem"
                />
                <MoreInCategorySection />
                <SectionSpacer
                    height={120}
                    mobileHeight={48}
                    className="bg-ground"
                    unit="rem"
                />
                <InTheWildSection />
                {/* What has been said about the supplier, under the projects
                    their product has been specified in. The projects' captions run
                    to the section's own edge, so the gap between the two is this
                    spacer rather than either one's padding. */}
                <SectionSpacer
                    height={120}
                    mobileHeight={48}
                    className="bg-ground"
                    unit="rem"
                />
                <ProductTestimonialSection />
                {/* The tagged-projects statement is an accordion row on desktop
                    now — "Specified in", after About the Supplier — so there is no
                    standalone block here. Mobile still renders its own
                    SpecifiedInSection below, where a click-to-expand row reads as
                    plain text. */}
                {/* The accordion's closing hairline, then a clear breath into
                    the page's last word: where to go next in this category.
                    Get Matched used to close here — it asked a reader who had read
                    the whole page to start again and describe the thing they were
                    already looking at. The three-part row keeps them moving
                    through the category instead. */}
                {/* No closing enquiry band: Get more Specific carries the
                    website and enquiry routes further up the page. */}
                {/* The section owns the long breath above it and, while it
                    renders, the end of the document — the footer stands down
                    so the scroll clamp lands on the grown row. A product
                    with no next one leaves no band here and keeps its
                    footer. */}
                <IslandFreeClosing>
                    <NextProductTransition next={nextProduct} />
                </IslandFreeClosing>
            </IslandFreeClosing>
            {/* Island-bound on desktop too: the enquiry fallback pill must not
                float over the closing or the site footer. */}
        </>
    );

    const desktopOrder = (
        <>
            <ProductHeroSection />
            {hasGallery ? (
                <>
                    {/* Short breath under the compact title — the gallery is
                        the page's real hero (and the morph destination), so
                        it sits as high in the fold as the title allows. */}
                    <SectionSpacer
                        height={48}
                        mobileHeight={24}
                        className="bg-ground"
                        unit="rem"
                    />
                    <ProductGallerySection />
                    {/* Straight after the gallery, so its half-height negative
                        margin measures against the picture's lower edge and the
                        badge straddles it. Below the breadcrumbs it measured
                        against those instead and barely overlapped. */}
                    <ProductSupplierLogoBadge />
                    <ProductBreadcrumbBar />
                </>
            ) : null}
            {desktopBody}
        </>
    );

    const mobileOrder = (
        <>
            {hasGallery ? (
                <>
                    <ProductGallerySection />
                    {/* Straight after the gallery, so its half-height negative
                        margin measures against the picture's lower edge and the
                        badge straddles it. Below the breadcrumbs it measured
                        against those instead and barely overlapped. */}
                    <ProductSupplierLogoBadge />
                    <SectionSpacer
                        height={64}
                        mobileHeight={28}
                        className="bg-ground"
                        unit="rem"
                    />
                </>
            ) : null}
            {/* The purchase block carries the title + inline selector on
                mobile — no text hero, no read-only meta row. */}
            <ProductPurchaseSection />
            {/* The picture only: its description, specs and facts have
                moved up to the purchase block's Read more. Its expand opens
                the same viewer the gallery at the top does. */}
            <ProductDetailsSection imageOnly />
            <ProductDownloadsSection />
            <ProductSpecAccordionSection runIn="max-md:pt-12 md:pt-[7.5rem]" />
            {/* Installers and the supplier's catalogue, out of the accordion
                and on the page under their own heads (Figma 149:13425). */}
            <ProductInstallersSection runIn="max-md:pt-6 md:pt-[3.75rem]" />
            {/* The trail and the tags, as the page's closing reference line
                just before it turns to other products: where this product
                sits, and the searches it belongs to, are the ways out. No spacer
                of its own: the band above closes on its own padding, which
                matches the air under the tags. */}
            {/* Desktop's two routes to the supplier — their website, and an
                enquiry — as stacked cards, ahead of the reference line. With
                the trail's own top padding, the section closes on the same air
                the band above leaves over it. */}
            <ProductGetSpecificSection mobileCardsOnly />
            <ProductBreadcrumbBar withStats={false} />
            {/* Tags only: Product Details sits inside the description, with
                the purchase block, behind its Read more. */}
            <ProductCreditsRow
                showDetails={false}
                showVariants={false}
                showSpecifiers={false}
                hideMobileTagsLabel
            />
            {/* The page's hairline, 40 above it here and 40 below it at the
                top of More products, closing the reference line off from the
                products that follow. */}
            <div className="bg-ground px-6 pt-10">
                <hr className="m-0 h-0 border-t border-[#e5e5e5]" />
            </div>
            {/* Once the reader is into the products that follow this one,
                the island stops offering this product and offers the next.
                One claim over the whole run: the carousels inside it make
                their own, and a claim per block flickers in the gaps. */}
            <IslandFreeClosing mode={nextProductIsland} priority={5}>
                <MoreProductsSection runIn="max-md:pt-10 md:pt-[11.3125rem]" />
                {/* (NN) Specified in — mobile's dedicated treatment of the tagged
                    projects (the desktop statement's click-to-expand reads as
                    plain text on touch). Owns its leading spacer and hairline,
                    like Similar Products below. */}
                {hasTaggedProjects ? <SpecifiedInSection /> : null}
                {/* The rest of the category, under the same two-line head the
                    supplier's range gets above it, as desktop sets it. */}
                {/* The page's hairline between the supplier's range and the
                    category, 40 either side as under the tags — 24 of it here,
                    the rest the Load More above carries under itself. */}
                <div className="bg-ground px-6 pt-6">
                    <hr className="m-0 h-0 border-t border-[#e5e5e5]" />
                </div>
                {/* The page's last section on a phone: 80 under its Load
                    More before the footer takes over. */}
                <MoreInCategorySection
                    lead="More products in"
                    runIn="max-md:pt-10 max-md:pb-20"
                />
                {/* No next-product turn on mobile: a flick's momentum keeps
                    scrolling through the input hold, so the turn lands the
                    next page already mid-scroll. The island's next-product
                    pill carries the same move instead, and the page ends
                    here exactly as one with nothing after it does. */}
            </IslandFreeClosing>
        </>
    );

    return (
        <VariantSelectionProvider product={product}>
            <VariantOptionProvider product={product}>
                <ProductPageCtasProvider>
                    <ProductViewTracker />
                    {/* Chevron tucked inside the gallery tile: 1rem page pad +
                    1rem inner inset, mirroring the save button top-right. */}
                    <DetailMobileAppChrome className="top-8 left-8" />
                    <ProductDetailsMagicIsland>
                        <main
                            id="main-content"
                            data-testid="product-page-revamp"
                            className={cn(
                                // The page's ground, declared once: every `bg-ground`
                                // band below inherits it instead of naming a colour.
                                'bg-ground ground-grey',
                                // Text-first mobile order (no gallery): clear the
                                // app-mode back chevron. Gallery-first pages let
                                // it overlay the first image instead.
                                !hasGallery && 'app-mode-top-clearance',
                            )}
                        >
                            {mobile ? mobileOrder : desktopOrder}
                        </main>
                    </ProductDetailsMagicIsland>
                    {/* Full-bleed pinch-to-zoom viewer behind every gallery and
                    diagram tap on mobile. Mounted outside the island provider
                    so it covers the page chrome, and inert (no chunk fetched)
                    until the first tap. */}
                    {/* Long page, and the accordion rows make it longer still.
                    Stacks above the chat dock on its own, same as the browse
                    grids. Desktop only: the island already owns the bottom of
                    the mobile viewport, and two floating controls there would
                    fight for the same corner. */}
                    {hydrated && !mobile ? <BackToTop /> : null}
                </ProductPageCtasProvider>
            </VariantOptionProvider>
        </VariantSelectionProvider>
    );
};
